// Job-row shapes — the export/tax-document job status row and the notifications
// feed row. Transcribed from `contracts-legacy/notifications-backend/schemas.ts`
// (job-row shapes of the exit gate). The bulk-action status row is NOT here: that
// surface is ported separately into `payments` per D-015 M-2/M-5 and its §6 rows
// belong to the payments contacts/bulk-action milestone.

import { z } from 'zod';

import { JOB_STATUSES, UUID_V4, presentKey, presentKeyMessage } from './shared.js';

/**
 * `GET /export/status/{jobId}` 200 body (`schemas.ts:307-339`). This route also
 * serves tax-document jobs — they share ExportJobsTable and there is no
 * `/tax-documents/status` route (dossier §2.2).
 *
 * The optionality is not cosmetic — each optional key is gated on a distinct
 * handler condition:
 *   jobId, status, entityType, recordCount, createdAt   ALWAYS (5 keys)
 *   completedAt   iff job.completedAt truthy
 *   error         iff job.error truthy OR stale OR presign threw
 *   processedCount/totalCount/successCount/failedCount  each iff `is not None`
 *   skippedCount  iff present on the row, any entity type, 0 included (CON-857; absent on old jobs)
 *   stale         iff stale_job_reason(job) returned copy (literal true; never false)
 *   downloadUrl   iff status == COMPLETED and s3Key present and presign succeeded
 *
 * `entityType` and `status` are `job.get(...)` with NO default, so a row missing
 * either yields JSON `null` — reachable, because four different start handlers and
 * the workers write this table. Both are nullable deliberately.
 */
export const exportStatusSchema = z
  .object({
    jobId: z.string(),
    status: z.union([z.enum(JOB_STATUSES), z.null()]),
    entityType: z.union([z.string(), z.null()]),
    recordCount: z.number().int(),
    createdAt: z.number().int(),
    completedAt: z.number().int().optional(),
    error: z.string().optional(),
    processedCount: z.number().int().optional(),
    totalCount: z.number().int().optional(),
    successCount: z.number().int().optional(),
    failedCount: z.number().int().optional(),
    /** CON-857: returned for any job row that has it (ZIP no-donations skips, email skips); absent on old jobs. */
    skippedCount: z.number().int().nonnegative().optional(),
    stale: z.literal(true).optional(),
    /** §6.25: the ONLY artifact-access field — presigned GET, 3600s, attachment disposition. */
    downloadUrl: z.string().optional(),
  })
  .strict()
  /**
   * The staleness override sets `status` to FAILED and `error` at the same time
   * (`schemas.ts:329-332`): `stale` can never appear without both.
   */
  .refine((b) => b.stale !== true || (b.status === 'FAILED' && typeof b.error === 'string'), {
    message: 'stale:true requires status FAILED and an error string',
  })
  /**
   * `downloadUrl` is only ever set inside `if job.status == 'COMPLETED' and s3Key`
   * (`schemas.ts:337-339`). The staleness override cannot co-occur — it only fires
   * for PENDING/PROCESSING.
   */
  .refine((b) => b.downloadUrl === undefined || b.status === 'COMPLETED', {
    message: 'downloadUrl requires status COMPLETED',
  });

export type ExportStatus = z.infer<typeof exportStatusSchema>;

/**
 * The 200 body of all four `<x>/start` routes (`schemas.ts:215-217`):
 * `job_id = str(uuid.uuid4())`, pinned as a real uuid4.
 */
export const jobAcceptedSchema = z.object({ jobId: z.string().regex(UUID_V4) }).strict();

export type JobAccepted = z.infer<typeof jobAcceptedSchema>;

/**
 * The row `create_notification` writes to NotificationsTable — the notifications
 * FEED row the dashboard reads back (`schemas.ts:1201-1240`). FOURTEEN keys, all
 * unconditional (`shared/notifications_table.py:39-54`).
 *
 * Only FOUR keys are writer-controlled and genuinely typed: `id`, `isRead`,
 * `createdAt`, `ttl`. The other TEN are parameters with NO type constraint at any
 * source site — they arrive from `notifications_writer/handler.py` off an untrusted
 * cross-repo queue payload, guarded only for Python truthiness. They are
 * `z.unknown()` + `presentKey`: any JSON value, but never ABSENT.
 */
export const notificationRowSchema = z
  .object({
    // --- writer-controlled: computed inside create_notification, genuinely typed ---
    /** `str(uuid.uuid4())` — `notifications_table.py:35`. */
    id: z.string().regex(UUID_V4),
    /** Literal `False` — `:51`. */
    isRead: z.boolean(),
    /** `int(time.time())` — `:36`. */
    createdAt: z.number().int(),
    /** `created_at + 30 days` — `:37` (§6.3's feed half). */
    ttl: z.number().int(),

    // --- passthrough: ten params, NO type constraint at any source site ---
    clerkUserID: z.unknown(),
    userEmail: z.unknown(),
    clerkOrganizationRole: z.unknown(),
    clerkOrganizationID: z.unknown(),
    merchantName: z.unknown(),
    actionType: z.unknown(),
    entityType: z.unknown(),
    status: z.unknown(),
    metadata: z.unknown(),
    actionLabel: z.unknown(),
  })
  .strict()
  .refine((r) => r.ttl === r.createdAt + 30 * 24 * 60 * 60, {
    message: 'ttl is createdAt + 30 days',
  })
  // All fourteen are UNCONDITIONAL keys of one dict literal, so absence stays
  // barred even though `z.unknown()` is implicitly optional in Zod 3.
  .refine(presentKey('clerkUserID', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('clerkUserID', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('userEmail', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('userEmail', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('clerkOrganizationRole', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('clerkOrganizationRole', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('clerkOrganizationID', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('clerkOrganizationID', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('merchantName', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('merchantName', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('actionType', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('actionType', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('entityType', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('entityType', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('status', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('status', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('metadata', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('metadata', 'shared/notifications_table.py:39-54'),
  })
  .refine(presentKey('actionLabel', 'shared/notifications_table.py:39-54'), {
    message: presentKeyMessage('actionLabel', 'shared/notifications_table.py:39-54'),
  });

export type NotificationRow = z.infer<typeof notificationRowSchema>;

/**
 * `DELETE /notifications/{id}` 200 body (`schemas.ts:544-548`).
 * `{'deleted': True, 'id': <the id from the path>}` — `delete_item` already ran.
 */
export const notificationDeletedSchema = z
  .object({ deleted: z.literal(true), id: z.string() })
  .strict();

export type NotificationDeleted = z.infer<typeof notificationDeletedSchema>;

/**
 * `PATCH /notifications/{id}/read` 200 body (`schemas.ts:598-602`).
 */
export const notificationMarkedReadSchema = z
  .object({ markedRead: z.literal(true), id: z.string() })
  .strict();

export type NotificationMarkedRead = z.infer<typeof notificationMarkedReadSchema>;

/**
 * `PATCH /notifications/read-all` 200 body (`schemas.ts:617-624`). NO `id` key and
 * NO count — even though the handler knows how many rows transitioned, the caller
 * is told nothing (D7, proposed, not applied).
 */
export const notificationsMarkedReadAllSchema = z.object({ markedRead: z.literal(true) }).strict();

export type NotificationsMarkedReadAll = z.infer<typeof notificationsMarkedReadAllSchema>;
